import { queryClient } from 'libs/query';
import { ILaboratorySummary, refreshToken, User } from 'models/user';
import { createContext, FC, ReactNode, useEffect, useReducer } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from 'store';
import { setSignedIn } from 'store/ducks/Signin';
import { selectLaboratory as selectLaboratoryRequest, signin } from 'store/ducks/Signin/service';
import axios, { axiosEndPoint } from 'utils/axios';

interface AuthState {
  isInitialized: boolean;
  isAuthenticated: boolean;
  user: User['user'] | null;
  laboratories: ILaboratorySummary[];
  requiresLaboratorySelection: boolean;
}

interface AuthContextValue extends AuthState {
  method: 'JWT';
  login: (email: string, password: string) => Promise<void>;
  selectLaboratory: (laboratoryId: string) => Promise<void>;
  logout: () => void;
  register: (email: string, name: string, password: string) => Promise<void>;
}

interface AuthProviderProps {
  children: ReactNode;
}

type InitializeAction = {
  type: 'INITIALIZE';
  payload: {
    isAuthenticated: boolean;
    user: User['user'] | null;
    laboratories?: ILaboratorySummary[];
    requiresLaboratorySelection?: boolean;
  };
};

type LoginAction = {
  type: 'LOGIN';
  payload: {
    user: User['user'];
    laboratories: ILaboratorySummary[];
    requiresLaboratorySelection: boolean;
  };
};

type SelectLaboratoryAction = {
  type: 'SELECT_LABORATORY';
  payload: {
    user: User['user'];
    laboratories: ILaboratorySummary[];
  };
};

type LogoutAction = {
  type: 'LOGOUT';
};

type RegisterAction = {
  type: 'REGISTER';
  payload: {
    user: User['user'];
  };
};

type Action =
  | InitializeAction
  | LoginAction
  | SelectLaboratoryAction
  | LogoutAction
  | RegisterAction;

const initialAuthState: AuthState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
  laboratories: [],
  requiresLaboratorySelection: false,
};

const setSession = (accessToken: string | null): void => {
  if (accessToken) {
    localStorage.setItem('accessToken', accessToken);
    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
  } else {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    delete axios.defaults.headers.common.Authorization;
  }
};

const handlers: Record<
  string,
  (state: AuthState, action: Action) => AuthState
> = {
  INITIALIZE: (state: AuthState, action: InitializeAction): AuthState => {
    const {
      isAuthenticated,
      user,
      laboratories = [],
      requiresLaboratorySelection = false,
    } = action.payload;

    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
      laboratories,
      requiresLaboratorySelection,
    };
  },
  LOGIN: (state: AuthState, action: LoginAction): AuthState => {
    const { user, laboratories, requiresLaboratorySelection } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
      laboratories,
      requiresLaboratorySelection,
    };
  },
  SELECT_LABORATORY: (
    state: AuthState,
    action: SelectLaboratoryAction,
  ): AuthState => ({
    ...state,
    user: action.payload.user,
    laboratories: action.payload.laboratories,
    requiresLaboratorySelection: false,
  }),
  LOGOUT: (state: AuthState): AuthState => ({
    ...state,
    isAuthenticated: false,
    user: null,
    laboratories: [],
    requiresLaboratorySelection: false,
  }),
  REGISTER: (state: AuthState, action: RegisterAction): AuthState => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
};

const reducer = (state: AuthState, action: Action): AuthState =>
  handlers[action.type] ? handlers[action.type](state, action) : state;

const AuthContext = createContext<AuthContextValue>({
  ...initialAuthState,
  method: 'JWT',
  login: () => Promise.resolve(),
  selectLaboratory: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
});

export const AuthProvider: FC<AuthProviderProps> = (props) => {
  const { children } = props;
  const [state, dispatch] = useReducer(reducer, initialAuthState);
  const appDispatch = useAppDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    const initialize = async (): Promise<void> => {
      try {
        // dispatch({
        //   type: 'INITIALIZE',
        //   payload: {
        //     isAuthenticated: true,
        //     user: {
        //       ID: 1,
        //       AVATAR: null,
        //       username: 'carlos.santos',
        //       CARGO: 'admin',
        //       POSTAGEM: null,
        //       EMAIL: '',
        //       RESPONSAVEL: 'admin',
        //       FUNCAO: 'admin',
        //       NAME: 'Carlos',
        //     },
        //   },
        // });
        const accessToken = window.localStorage.getItem('accessToken');

        if (accessToken) {
          const currentRefreshToken =
            window.localStorage.getItem('refreshToken');

          const response = await axiosEndPoint.post<refreshToken>(
            '/authentication/refresh',
            { REFRESHTOKEN: currentRefreshToken },
          );
          const { token, user, laboratories, requiresLaboratorySelection } =
            response.data;

          setSession(token);
          appDispatch(setSignedIn(user));
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: true,
              user,
              laboratories,
              requiresLaboratorySelection,
            },
          });
        } else {
          dispatch({
            type: 'INITIALIZE',
            payload: {
              isAuthenticated: false,
              user: null,
            },
          });
        }
      } catch (err) {
        console.error(err);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, [appDispatch]);

  const login = async (email: string, password: string): Promise<void> => {
    const response = await signin({ email, password });

    const {
      accessToken,
      currentRefreshToken,
      user,
      laboratories,
      requiresLaboratorySelection,
    } = response;
    localStorage.setItem('refreshToken', currentRefreshToken);
    appDispatch(setSignedIn(user));
    setSession(accessToken);
    dispatch({
      type: 'LOGIN',
      payload: {
        user,
        laboratories,
        requiresLaboratorySelection,
      },
    });
  };

  const selectLaboratory = async (laboratoryId: string): Promise<void> => {
    const isSwitching = Boolean(state.user?.laboratoryId);
    const { accessToken, currentRefreshToken, user, laboratories } =
      await selectLaboratoryRequest(laboratoryId);

    localStorage.setItem('refreshToken', currentRefreshToken);
    setSession(accessToken);

    if (isSwitching) {
      // Nothing cached may leak from the previous laboratory. App remounts
      // its routes when the laboratory changes (keyed on laboratoryId), so
      // component state is rebuilt too - all without reloading the page.
      queryClient.clear();
    }

    appDispatch(setSignedIn(user));
    dispatch({ type: 'SELECT_LABORATORY', payload: { user, laboratories } });

    if (isSwitching) navigate('/', { replace: true });
  };

  const logout = async (): Promise<void> => {
    setSession(null);
    dispatch({ type: 'LOGOUT' });
  };

  const register = async (
    email: string,
    name: string,
    password: string,
  ): Promise<void> => {
    const response = await axios.post<{
      accessToken: string;
      user: User['user'];
    }>('/api/account/register', {
      email,
      name,
      password,
    });
    const { accessToken, user } = response.data;

    window.localStorage.setItem('accessToken', accessToken);

    dispatch({
      type: 'REGISTER',
      payload: {
        user,
      },
    });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'JWT',
        login,
        selectLaboratory,
        logout,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
